import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import Button from '@amzn/meridian/button'
import Column from '@amzn/meridian/column'
import Text from '@amzn/meridian/text'
import Divider from '@amzn/meridian/divider'

import ATLoader from '@components/ATLoader'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { CrossFrameUtils, getLocalizationContext } from '@amzn/jwo-store-management-platform-components'
import { t } from '@src/translationHelper'
import { getDeviceId } from '@utils/DeviceUtil'
import { fetchMenuConfig } from '@utils/Menu/MenuUtil'
import { validateDomainAndRegion } from '@utils/ProvisionUtil'
import { loadToolRegistrations } from '@crossframe/apis/ToolRegistration'
import { IhmLoginNotLoggedInException } from '@utils/Exceptions/IhmLoginExceptions'
import { UserTypeSelectionRequiredException } from '@utils/Exceptions/BaseToolCommonExceptions'
import { ProvisioningConstants } from '@utils/Constants/ProvisioningConstants'
import { getIsSeawardFrameLoading, getSeawardIFrame, getSeawardProvisionError } from '@atredux/selectors/iframeSelector'
import { seawardIFrameLoadingStatus } from '@atredux/slices/iframeSlice'
import { store } from '@atredux/store'
import * as FeatureConfigService from '@services/FeatureConfigService'
import { StoreIdServiceFor3P } from '@pages/StoreIdSelectionFor3P/utils/StoreIdServiceFor3P'
import { useBundle, useLocalizationContext } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { setIsToolRegistryLoaded } from '@src/services/HeliosInterruptService'
import { IIBServiceClient } from '@api/IIBServiceClient'
import './Provision.scss'
import { initNotificationCount } from '@src/utils/NotificationUtil'
import { getSelectedLocale } from '@src/redux/selectors/localeSelector'
import { Orchestrator } from '@src/orchestration/manager'

/**
 * Component handling the initial provisioning of the tool including getting deviceID, validation of domain
 * & store and loading the toolRegistration and feature config file.
 */
const ProvisionPage = () => {
    const seawardFrameStatus: seawardIFrameLoadingStatus = useSelector(getIsSeawardFrameLoading)
    const seawardProvisioningError: string = useSelector(getSeawardProvisionError)
    const [isMakingServiceCall, setIsMakingServiceCall] = useState(false)
    const [provisioningError, setProvisioningError] = useState(undefined)
    const navigate = useNavigate()
    const { setLocalizationContext } = useLocalizationContext()
    const [bundle, isBundleLoading] = useBundle(BUNDLE_NAME)

    useEffect(() => {
        if (seawardFrameStatus === seawardIFrameLoadingStatus.LOADING) {
            console.log('JWOStoreManagementPlatform: Loading seaward frame')
            return
        }
        if (seawardFrameStatus === seawardIFrameLoadingStatus.FAILED && seawardProvisioningError) {
            console.error('JWOStoreManagementPlatform: Seaward frame faced provisioning error :', seawardProvisioningError)
            setProvisioningError(seawardProvisioningError)
            return
        }
        getDeviceId().then(() => {
            configure()
        })
    }, [seawardFrameStatus])

    const configure = async () => {
        if (seawardFrameStatus === seawardIFrameLoadingStatus.FAILED) {
            console.warn('Attempted to configure after facing provisioningError. Sending Retry CrossFrame call to Seaward')
            CrossFrameUtils.sendMessageToOtherFrame({
                frame: getSeawardIFrame(store.getState())?.current?.contentWindow,
                message: {
                    method: 'retrySeawardConfigure',
                },
            })
            return
        }
        setIsMakingServiceCall(true)
        setProvisioningError(undefined)

        let promise = validateDomainAndRegion()

        // Use new menu API to avoid Seaward iframe dependency (preserves working behavior from before migration)
        const isGetMenuAPIEnabled = true

        if (!isGetMenuAPIEnabled) {
            promise = promise.then(loadToolRegistrations)
        }

        promise
            .then(() => setLocalizationContext(getLocalizationContext(store?.getState().locale ? getSelectedLocale(store.getState()) : 'en-US')))
            .then(fetchMenuConfig)
            .then(FeatureConfigService.load)
            .then(() => {
                // Initialize CloudFront signed cookies for asset access BEFORE landing on /home
                // (P494833843): return the promise so the chain awaits it, guaranteeing the
                // CloudFront-Policy/Signature/Key-Pair-Id cookies are set before any /assets/* tool
                // iframe renders — otherwise the first asset request is unsigned and CloudFront
                // returns the XML "MissingKey" error (a refresh then works). Fail-open on error so a
                // cookie-fetch failure logs but never blocks provisioning/landing. IFrameController
                // also gates on cookie readiness as defense-in-depth for entry paths that bypass
                // provisioning (deep links, notifications).
                return import('@services/AssetCloudFrontCookieService')
                    .then((m) => m.default.init())
                    .catch((error) => console.error('JWOStoreManagementPlatform: Failed to initialize CloudFront signed cookies', error))
            })
            .then(async () => {
                // Hardcoded: WeblabService removed during CrossFrameUtils migration
                // Weblab ATBASE_TELEMETRY_1248304 is T1 100% in development
                const isTelemEnabled = true
                console.log('JWOStoreManagementPlatform: Telem enabled', isTelemEnabled)
                Orchestrator.instance.setWeblabEnabled(isTelemEnabled)
            }).then(async () => {
                // Associate Entry tool loading is handled by StoreSelectionSheetController
                // after a store is selected. No need to dispatch switchTool here since
                // the store ID is not yet available during provisioning.
            })
            .then(() => {
                console.log('JWOStoreManagementPlatform: Done loading tool registrations.')
                setIsToolRegistryLoaded(true)
                navigate('/home')
            })

            .catch((exception) => {
                if (exception instanceof IhmLoginNotLoggedInException) {
                    // The service call in StoreService.setCurrentStore will trigger IhmLogin.
                    // Don't show an error while waiting.
                    console.debug('Got IhmLoginNotLoggedInException. Ignoring...')
                } else if (exception instanceof UserTypeSelectionRequiredException) {
                    console.debug('Redirecting to User Choice interstitial')
                    navigate('/user-type-selection')
                } else {
                    console.error('JWOStoreManagementPlatform: Provisioning error', exception)
                    const errorMessage = exception.getMessage ? exception.getMessage() :
                            exception.message ? exception.message :
                            String(exception)
                    setProvisioningError((t(bundle, 'ihm-associatetoolsbase-privisioning-app-setup-error') + errorMessage))
                }
            }).finally(() => {
                setIsMakingServiceCall(false)
                console.log('JWOStoreManagementPlatform: Finished initializing NotificationTaskService')
                initNotificationCount()
            })
    }

    const logout = () => {
        console.debug('Provision: Navigating to logout')
        setProvisioningError(null)
        setIsMakingServiceCall(true)
        if (EnvironmentDetail.isIhmIdentityBrokerAuthN()) {
            // Resetting store details when logout
            StoreIdServiceFor3P.resetStoreIdServiceFor3P()
        } else if (EnvironmentDetail.isIIBAuthN()) {
            IIBServiceClient.logout()
        } else {
            navigate('/logout')
        }
    }

    if (isBundleLoading) {
        return <ATLoader />
    }

    return (
        <Column spacing={'medium'} spacingInset="medium">
            {((seawardFrameStatus === seawardIFrameLoadingStatus.LOADING || isMakingServiceCall) && !provisioningError) &&
                <ATLoader />
            }
            {(!!provisioningError || seawardFrameStatus === seawardIFrameLoadingStatus.FAILED) && (
                <div className="provisioning-error">
                    <Text type="h300" alignment={'center'} color={'error'}>
                        {ProvisioningConstants.ERROR_HEADER_TEXT}
                    </Text>
                    <Divider />
                    <Text type="h100" alignment="left"> {provisioningError} </Text>
                    <Text type="h100" alignment="left"> {t(bundle, 'ihm-associatetoolsbase-provisioning-error-instructions')} </Text>
                    <Button type="primary" id="retry-configure-button"
                        onClick={configure}> {t(bundle, 'migrated-ihm-seaward-provisioning-retry')} </Button>
                    <Button type="secondary" id="logout-button"
                        onClick={logout}> {t(bundle, 'migrated-ihm-seaward-provisioning-retry-auth')} </Button>
                </div>
            )}
        </Column>
    )
}
export default ProvisionPage
